import { useContext, useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'

import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import useSearchAnalytics from '~/hooks/useSearchAnalytics'
import { useArtworks } from '~/services/query'
import { useDebounce } from '~/services/useDebounce'
import {
  PushInteractionLocation,
  PushInteractionType,
} from '~/utils/posthogEvents'

import type { ArtWorksProps } from './Artworks'

const useHooks = (
  queryId: ArtWorksProps['queryId'],
  type: ArtWorksProps['type'],
  fallbackData: ArtWorksProps['fallbackData'],
  filters: ArtWorksProps['filters'],
  searchTerm: ArtWorksProps['searchTerm'],
  slugParams: ArtWorksProps['slugParams'],
  setFacets: ArtWorksProps['setFacets'],
  setIsloading: ArtWorksProps['setIsloading'],
  pagenumber: ArtWorksProps['pagenumber'],
  pageSize: ArtWorksProps['pageSize']
) => {
  const [currentFilters, setCurrentFilters] = useState(filters)
  const [debouncedSearchTerm] = useDebounce<string>(
    searchTerm ? searchTerm : ''
  )
  const artworksElRef = useRef<HTMLDivElement>(null)
  const [firstRender, setIsFirstRender] = useState(true)

  // Track current page locally for slug-based pagination
  const [currentPage, setCurrentPage] = useState(pagenumber || 1)

  const { handleSearchPageLoad, handleSearchResultsClicked } =
    useSearchAnalytics()

  let total = 0
  let insightsQueryID
  const { data, error, size, setSize, isValidating, facets, tradingView } =
    useArtworks(
      debouncedSearchTerm,
      queryId,
      type,
      currentFilters,
      fallbackData,
      pageSize
    )

  const router = useRouter()
  const pageNumRef = useRef(pagenumber)

  const artworks =
    data && data.length
      ? data?.reduce((accumulator: any, value: any) => {
          total = value?.total
          insightsQueryID = value?.insightsQueryID
          return accumulator.concat(value.hits)
        }, [])
      : fallbackData

  useEffect(() => {
    setIsFirstRender(false)
  }, [])

  // Sync currentPage with pagenumber prop when it changes
  useEffect(() => {
    if (pagenumber && pagenumber !== currentPage) {
      setCurrentPage(pagenumber)
    }
  }, [pagenumber])

  useEffect(() => {
    if (!data) return
    setFacets?.(facets, total)
  }, [facets])

  useEffect(() => {
    if (!pageNumRef || pageNumRef.current != currentPage) {
      if (currentPage) setSize(currentPage)
      pageNumRef.current = currentPage
    }
  }, [currentPage])

  useEffect(() => {
    if (!data) return
    if (JSON.stringify(currentFilters) !== JSON.stringify(filters)) {
      if (currentPage) setSize(currentPage)
      setCurrentFilters(filters)
    }
  }, [filters, currentPage])

  useEffect(() => {
    if (!artworksElRef.current || firstRender) return

    // if (pagenumber === 1) {
    //   // Filters have changed and we are on page 1
    //   // setClickedInPageNav(true)

    //   const offset =
    //     artworksElRef.current.getBoundingClientRect().top +
    //     document.documentElement.scrollTop -
    //     225
    //   window.scrollTo({
    //     top: offset,
    //     behavior: 'smooth',
    //   })

    //   return
    // }
  }, [currentFilters, currentPage, router.query.aw])

  const loadMoreHandler = (
    e:
      | React.MouseEvent<HTMLLinkElement, MouseEvent>
      | React.KeyboardEvent<HTMLLinkElement>
  ) => {
    e.preventDefault()

    // Are we serving results through a slug-based url /slug/page/2
    const routerParams = {} as any
    if (slugParams) {
      const currentPath = router.asPath.split('?')[0].split('/page/')[0]
      const nextPage = currentPage + 1

      const routerQueryParams = { ...router.query }
      delete routerQueryParams.slug
      delete routerQueryParams.lang
      delete routerQueryParams.page
      routerParams['query'] = routerQueryParams

      // Check if this is a genre page - check the slug from router.query
      const slug = router.query.slug as string
      const isGenrePage = slug && slug.startsWith('genre-')

      if (isGenrePage) {
        // For genre pages, construct the public URL directly from the slug
        const publicPath = `/${slug}`
        const nextPageUrl = `${publicPath}/page/${nextPage}`

        // Build query string if there are params
        const queryString =
          Object.keys(routerQueryParams).length > 0
            ? '?' +
              new URLSearchParams(
                routerQueryParams as Record<string, string>
              ).toString()
            : ''

        // Use history.pushState for genre pages to avoid URL rewriting issues
        if (typeof window !== 'undefined') {
          window.history.pushState(null, '', nextPageUrl + queryString)
        }

        // Update local page tracking
        setCurrentPage(nextPage)

        // Still trigger the data fetch by updating the size
        setSize(nextPage)
      } else {
        // For non-genre pages (artist, etc), use router.push to update canonical
        router.push(
          {
            pathname: `${currentPath}/page/${nextPage}`,
            query: { ...routerQueryParams },
          },
          undefined,
          {
            shallow: true,
            scroll: false, // Prevent automatic scrolling
          }
        )

        // Update local page tracking
        setCurrentPage(nextPage)
      }

      return
    } else {
      // Handle query parameter-based pagination
      const nextPage = currentPage + 1
      const routerQueryParams = { ...router.query }

      // Update the page query parameter
      routerQueryParams.p = String(nextPage)

      // Use router.push to update URL with query parameters
      router.push(
        {
          pathname: router.pathname,
          query: routerQueryParams,
        },
        undefined,
        {
          shallow: true,
          scroll: false, // Prevent automatic scrolling
        }
      )

      // Update local page tracking
      setCurrentPage(nextPage)

      // Trigger the data fetch by updating the size
      setSize(nextPage)
    }
  }

  // Calculate the correct public-facing URL for the next page
  let nextPageUrl
  if (slugParams) {
    // Check if this is a genre page using the slug
    const slug = router.query.slug as string
    if (slug && slug.startsWith('genre-')) {
      // For genre pages, construct URL directly from slug
      nextPageUrl = `/${slug}/page/${currentPage + 1}`
    } else {
      // For other pages (artist, etc), use the current path
      const currentPath = router.asPath?.split('?')[0]?.split('/page/')[0]
      nextPageUrl = `${currentPath}/page/${currentPage + 1}`
    }
  } else {
    nextPageUrl = `${router.asPath?.split('?')[0]}?p=${size + 1}`
  }

  const isLoadingInitialData = !data && !error
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty = artworks?.length === 0

  useEffect(() => {
    const timer = setTimeout(
      setIsloading ? () => setIsloading(!data) : () => ({}),
      1000
    )
    return () => {
      clearTimeout(timer)
    }
  }, [data])

  // to avoid prop drilling / hoisting - update a global state with total number of results.
  const { stateDispatch } = useContext(GlobalStoreContext)
  useEffect(() => {
    if (total) {
      // put page loaded analytics based on total changing
      if (debouncedSearchTerm) {
        handleSearchPageLoad(
          debouncedSearchTerm,
          {
            numArtistResults: 0,
            numArtworkResults: total,
            numCollectionResults: 0,
            numArticleResults: 0,
          },
          PushInteractionLocation.InnerPageSearch,
          PushInteractionType.DynamicPageLoad
        )
      }

      stateDispatch({
        type: GlobalStoreProps.UPDATE_ARTWORKS_CATALOGUE_TOTAL,
        payload: total,
      })
    }
  }, [debouncedSearchTerm, total])

  return {
    isValidating,
    insightsQueryID,
    size,
    artworks,
    isEmpty,
    isLoadingMore,
    isLoadingInitialData,
    total,
    loadMoreHandler,
    nextPageUrl,
    data,
    artworksElRef,
    tradingView,
    handleSearchResultsClicked,
  }
}

export default useHooks
